/**
 * Redaction primitives.
 *
 * Exactly two categories are removed: **credentials**, and **content a person typed or a subject
 * provided**. Everything structural stays — identifiers, route params, study config values, request
 * paths, status codes, timings. An id is a surrogate key, not PHI, and a report that cannot say which
 * study, which case and which config it came from cannot be acted on.
 *
 * The line to hold: ids, config and structure in; values out. `caseId=c3d4` yes, `?search=Smith` no.
 * If a field can hold something a person typed into a form, only its *name* goes in the report.
 */

/**
 * Query parameters kept verbatim, by name. Everything else is replaced with `DROPPED` rather than
 * deleted, so a report still shows *that* a filter was applied without showing what it matched.
 *
 * Taken from the URI builders in `src/services/*` and `ENTRY_DIALOG_QUERY`. Note what is deliberately
 * absent: `search`, `token`, `branch_key` and `url` — the free-text and credential carriers.
 */
export const SAFE_ID_PARAMS: readonly string[] = [
  'studyId', 'studyIdentifier', 'caseId', 'caseIdentifier', 'entryId', 'entryIdentifier',
  'formId', 'formIdentifier', 'identifier', 'groupId', 'userId', 'inviteId', 'exportId', 'scanId',
  'page', 'limit', 'offset', 'mode', 'sortBy', 'sortOrder', 'pumpFilter', 'type', 'variable',
  'width', 'whatFor', 'withUsers', 'next'
];

/** Placeholder for a value we chose not to carry. Visible on purpose — silence would read as absence. */
export const DROPPED = '[dropped]';

/**
 * Credential field names, normalized (lowercased, separators stripped).
 *
 * Matching is on the **whole normalized name**, never a substring. An earlier draft used a substring
 * regex containing `key` and `data`, which would have silently blanked `keyId`, `dataViewFilter` and
 * `metadata` — quietly destroying the very fields a report needs. Over-redaction is as real a failure
 * as under-redaction, so every name here is exact.
 *
 * `key` and `data` are the two `Keys` fields holding HMAC signing material (`src/models/login.ts`).
 * They are safe to list because the match is exact: `keyId` and `dataViewFilter` do not hit it.
 */
export const CREDENTIAL_KEY_NAMES: ReadonlySet<string> = new Set([
  'authorization', 'xdate', 'cookie', 'setcookie',
  'password', 'passwd', 'pwd', 'pin', 'part11pin',
  'secret', 'clientsecret', 'codeverifier', 'signature', 'hmac',
  'token', 'accesstoken', 'refreshtoken', 'oauthaccesstoken', 'apikey', 'branchkey',
  'credential', 'credentials',
  'key', 'data'
]);

/** Response headers worth keeping. Everything else is dropped rather than reasoned about. */
const SAFE_HEADERS: readonly string[] = ['content-type', 'x-request-id'];

const normalizeKey = (name: string): string => name.toLowerCase().replace(/[-_\s]/g, '');

/**
 * Whether a field name denotes a credential.
 *
 * Explicitly out of scope: anything ending in `Identifier` or `Id`, and every `flags.*` value. Those
 * are what make an issue reproducible.
 */
export const isCredentialKey = (name: string): boolean => CREDENTIAL_KEY_NAMES.has(normalizeKey(name));

/** Whether a query parameter carries an identifier we are willing to put in a report. */
export const isSafeIdParam = (name: string): boolean => SAFE_ID_PARAMS.includes(name) && !isCredentialKey(name);

export const truncate = (value: string | undefined, max: number): string | undefined => {
  if (value == null) {
    return undefined;
  }
  return value.length > max ? `${value.slice(0, max)}…` : value;
};

export interface RedactedUrl {
  path?: string;
  params?: Record<string, string>;
}

/**
 * Split a url into its pathname and an allowlisted query.
 *
 * The pathname is kept **with its identifiers** (`/investigation/a1b2/case/c3d4`) rather than
 * templated to `:id` — those segments are how support finds the record.
 *
 * A repeated param keeps every value, comma-joined, matching `redactQuery`: `?caseId=a&caseId=b`
 * describes a request over two records, and last-one-wins would have quietly reported it as one.
 */
export const redactUrl = (url?: string): RedactedUrl | undefined => {
  if (!url) {
    return undefined;
  }

  try {
    // A base is required for relative urls and is discarded either way — only the path and query are read.
    const parsed = new URL(url, 'http://redacted.invalid');
    const params: Record<string, string> = {};

    parsed.searchParams.forEach((value, name) => {
      if (!isSafeIdParam(name)) {
        params[name] = DROPPED;
        return;
      }
      const seen = params[name];
      params[name] = seen == null ? value : `${seen},${value}`;
    });

    return {
      path: parsed.pathname,
      params: Object.keys(params).length > 0 ? params : undefined
    };
  } catch {
    // An unparseable url is not worth guessing at — keep the shape, drop the content.
    return { path: DROPPED };
  }
};

/**
 * The same allowlist as `redactUrl`, applied to a router query object.
 *
 * A route query is no safer than a request query: `?search=Smith` on a case list is typed-in content,
 * and a `token` in a magic link is a credential. Values reach the report only for allowlisted
 * identifier names; every other name survives with `DROPPED` in place of its value.
 */
export const redactQuery = (query?: Record<string, unknown>): Record<string, string> | undefined => {
  if (!query) {
    return undefined;
  }

  const safe: Record<string, string> = {};
  Object.entries(query).forEach(([name, value]) => {
    if (!isSafeIdParam(name)) {
      safe[name] = DROPPED;
      return;
    }
    // vue-router hands back `string | null | (string | null)[]` for repeated params.
    safe[name] = Array.isArray(value) ? value.map(item => String(item ?? '')).join(',') : String(value ?? '');
  });

  return safe;
};

/**
 * A `fullPath` reduced to its path plus an allowlisted query, for the activity trail.
 *
 * The trail leaves the app — it is embedded in the Sentry event and in the email fallback — so a
 * route label cannot carry the raw querystring the user happened to be on.
 */
export const redactFullPath = (fullPath?: string): string => {
  const redacted = redactUrl(fullPath);
  if (!redacted) {
    return DROPPED;
  }
  const path = redacted.path ?? DROPPED;
  if (!redacted.params) {
    return path;
  }
  // Joined by hand rather than through URLSearchParams: this is a label to read, not a url to fetch,
  // and percent-encoding `[dropped]` only makes it harder to scan.
  const query = Object.entries(redacted.params).map(([name, value]) => `${name}=${value}`).join('&');
  return query ? `${path}?${query}` : path;
};

export const redactHeaders = (headers?: Record<string, unknown>): Record<string, string> => {
  const safe: Record<string, string> = {};
  if (!headers) {
    return safe;
  }

  Object.entries(headers).forEach(([name, value]) => {
    if (SAFE_HEADERS.includes(name.toLowerCase()) && typeof value === 'string') {
      safe[name.toLowerCase()] = truncate(value, 200) as string;
    }
  });

  return safe;
};

/**
 * The key names inside an object, without any of its values.
 *
 * "The payload was missing `timepoint`" is often the whole diagnosis, and field names are schema
 * rather than subject data — so names travel and values never do.
 */
export const objectKeyPaths = (value: unknown, maxDepth: number = 2, limit: number = 50): string[] => {
  const paths: string[] = [];

  const walk = (node: unknown, prefix: string, depth: number): void => {
    if (paths.length >= limit || depth > maxDepth || node == null || typeof node !== 'object') {
      return;
    }

    if (Array.isArray(node)) {
      // One representative element: a 200-row array of the same shape says nothing extra.
      walk(node[0], `${prefix}[]`, depth);
      return;
    }

    Object.keys(node as Record<string, unknown>).forEach(name => {
      if (paths.length >= limit) {
        return;
      }
      const path = prefix ? `${prefix}.${name}` : name;
      paths.push(path);
      walk((node as Record<string, unknown>)[name], path, depth + 1);
    });
  };

  walk(value, '', 0);
  return paths;
};

/**
 * Deep-copy a value with credential fields blanked, long strings capped and cycles broken.
 *
 * Used on the parts of a Sentry event we do not author, where an integration may have attached
 * something we never chose to send.
 */
export const redactValue = (value: unknown, depth: number = 0, seen: WeakSet<object> = new WeakSet()): unknown => {
  if (depth > 6) {
    return '[depth]';
  }
  if (typeof value === 'string') {
    return truncate(value, 512);
  }
  if (value == null || typeof value !== 'object') {
    return value;
  }
  if (seen.has(value as object)) {
    return '[circular]';
  }
  seen.add(value as object);

  if (Array.isArray(value)) {
    return value.slice(0, 50).map(item => redactValue(item, depth + 1, seen));
  }

  const result: Record<string, unknown> = {};
  Object.entries(value as Record<string, unknown>).forEach(([name, nested]) => {
    result[name] = isCredentialKey(name) ? '[redacted]' : redactValue(nested, depth + 1, seen);
  });
  return result;
};
